import { useQuery } from 'react-query';
import axios from 'axios';
import { useMemo } from 'react';

const getNFTData = async (contractAddress, pageNumber, nftListFilter, rarity) => {
    const priceFilter = (() => {
        switch (nftListFilter) {
            case "Lowest":
                return "startingPrice";
            case "Latest":
                return "startAt";
            case "Highest":
                return "startingPrice";
            default:
                return "startingPrice";
        }
    })();
    const filter = nftListFilter === "Lowest" ? 1 : -1;
    try {
        const response = await axios.get(
            `${process.env.REACT_APP_MARKETPLACE_BACK_URL}/auction?contractAddress=${contractAddress}&page=${pageNumber}&sortField=${priceFilter}&filter=${filter}&rarity=${rarity}`
        );

        if (response?.data) {
            // console.log('Auction data fetched:', response.data);

            // Extract token IDs from the auction data response
            const nftIds = response.data.items.map(f => f.tokenID);
            // console.log(`${contractAddress} token IDs:`, nftIds);

            // Now, fetch the NFT details based on the token IDs
            let nftDetailsData = { items: [] };
            try {
                const res = await axios.post(
                    `${process.env.REACT_APP_MARKETPLACE_BACK_URL}/getNFT`,
                    { contractAddress, ids: nftIds }
                );
                nftDetailsData = res?.data || { items: [] };
            } catch (e) {
                console.warn('getNFT failed, using auction data only:', e?.message);
            }

            const detailItems = nftDetailsData?.items ?? [];

            const mergedData = response.data.items.map((auctionItem) => {
                const nftDetail = detailItems.find(
                    (detail) => String(detail?.tokenID ?? detail?.tokenId) === String(auctionItem?.tokenID)
                );
                if (nftDetail) {
                    return {
                        ...auctionItem,
                        ...nftDetail,
                        name: nftDetail.name ?? auctionItem.name,
                        image: nftDetail.image ?? '',
                        attributes: nftDetail.attributes ?? [],
                    };
                }
                return {
                    ...auctionItem,
                    name: auctionItem.name ?? `NFT #${auctionItem?.tokenID ?? ''}`,
                    image: auctionItem.image ?? '',
                    attributes: auctionItem.attributes ?? [],
                };
            });

            // Return the merged data
            // console.log('Merged data:', mergedData);
            return mergedData;
        }
    } catch (error) {
        console.error(`Error fetching data for ${contractAddress}:`, error);
        throw error;
    }
};

export const useNFTData = (contractAddress, pageNumber, nftListFilter, rarity, nftType) => {
    const { data, isLoading, error } = useQuery(
        ['nftData', contractAddress, pageNumber, nftListFilter, rarity],
        () => getNFTData(contractAddress, pageNumber, nftListFilter, rarity),
        {
            enabled: !!contractAddress,
            retry: 2,
            staleTime: 30 * 60 * 1000, // 30 minutes
            cacheTime: 60 * 60 * 1000, // 60 minutes
            // Add this to persist cache between page refreshes
            structuralSharing: true
        }
    );

    const filteredData = useMemo(() => {
        if (nftType && nftType.length > 0 && data) {
            return data.filter(item =>
                nftType.some(type => item?.name?.toLowerCase().includes(type.toLowerCase()))
            );
        }
        return data;
    }, [data, nftType]);

    return { data: filteredData, isLoading, error };
};
